// @greenlight-spec: 12-azure-installation.md
// @greenlight-spec: 28-it-dashboard-ui.md

import * as React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Stepper } from '@/components/install/Stepper';
import { DRAFT_REREAD_MS } from '@/components/install/installDraft';
import type { StepConfig } from '@/components/install/Stepper';
import {
  InstallStepGroupsContext,
  deriveCurrentStep,
  groupForStep,
  isLastStepOfGroup,
  nextGroupId,
  stepGroupsFor,
  type InstallStepGroupId,
} from '@/components/install/stepGroups';
import { OrgSetupStep } from '@/components/install/steps/OrgSetupStep';
import { DnsHttpsStep } from '@/components/install/steps/DnsHttpsStep';
import { GitHubAppStep } from '@/components/install/steps/GitHubAppStep';
import { PluginMarketplaceStep } from '@/components/install/steps/PluginMarketplaceStep';
import { IdpStep } from '@/components/install/steps/IdpStep';
import { ContactStep } from '@/components/install/steps/ContactStep';
import { PaymentStep } from '@/components/install/steps/PaymentStep';
import { SummaryStep } from '@/components/install/steps/SummaryStep';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { api } from '@/lib/api';
import { errorBodyCode, thrownCode } from '@/lib/apiErrors';
import { useRefetchOnFocus } from '@/lib/useRefetchOnFocus';
import { INSTALL_STEPS, installStepApplies } from '@greenlight/shared';
import type {
  AgentKind,
  BillingProviderType,
  InstallStep,
  InstallationState,
} from '@greenlight/shared';
import type { InstallDraft } from '@greenlight/contract';

/** Result of advancing a wizard step: the failure `code` lets a step show a
 * recovery action specific to why the server refused, not just that it did. */
interface AdvanceResult {
  ok: boolean;
  code?: string;
}

/**
 * True when a step cannot be acted on yet. Free-roam navigation still lets IT
 * open it as a read-only preview; the step renders its own blocked notice.
 */
function isBlocked(
  stepId: InstallStep,
  state: InstallationState | undefined,
  billingProvider: BillingProviderType,
): boolean {
  const steps = state?.steps;
  if (stepId === 'verified_url_handoff') return steps?.control_plane_https?.status !== 'complete';
  if (stepId === 'idp' || stepId === 'github_app') {
    return steps?.verified_url_handoff?.status !== 'complete';
  }
  if (stepId === 'plugin_marketplace') return steps?.github_app?.status !== 'complete';
  // Finishing writes `installed_at`, so it needs every other step done first.
  if (stepId === 'summary') {
    return INSTALL_STEPS.some(
      (s) =>
        s !== 'summary' &&
        installStepApplies(s, billingProvider) &&
        steps?.[s]?.status !== 'complete',
    );
  }
  return false;
}

/** A group is blocked only when every step it presents is blocked. */
function isGroupBlocked(
  groupSteps: readonly InstallStep[],
  state: InstallationState | undefined,
  billingProvider: BillingProviderType,
) {
  return groupSteps.every((s) => isBlocked(s, state, billingProvider));
}

function recordsAlreadyUpserted(state: InstallationState | undefined): boolean {
  if (state?.steps?.dns?.status === 'complete') return true;
  return state?.steps?.dns?.details?.['records_upserted'] === true;
}

const DRAFTED_GROUPS = ['organization', 'idp', 'github_app', 'contact'] as const;
type DraftedGroup = (typeof DRAFTED_GROUPS)[number];

function isDraftedGroup(id: InstallStepGroupId): id is DraftedGroup {
  return (DRAFTED_GROUPS as readonly string[]).includes(id);
}

export function InstallPage() {
  const navigate = useNavigate();

  const {
    data: contextData,
    isLoading: contextLoading,
    isError: contextError,
    refetch: refetchContext,
  } = api.install.getContext.useQuery(['install-context'], {});

  const context = contextData?.status === 200 ? contextData.body : null;
  const draft: InstallDraft = context?.draft ?? {};
  const orgExists = context?.orgExists ?? false;
  const billingProvider: BillingProviderType = context?.billingProvider ?? 'direct';
  const groups = React.useMemo(() => stepGroupsFor(billingProvider), [billingProvider]);
  const [searchParams] = useSearchParams();
  const paymentReturned = searchParams.get('payment') === 'returned';

  const {
    data: statusData,
    isLoading: statusLoading,
    isError: statusError,
    refetch: refetchStatus,
  } = api.install.getStatus.useQuery(
    ['install-status'],
    {},
    { queryKey: ['install-status'], enabled: orgExists },
  );

  const createOrgMutation = api.install.createOrg.useMutation();
  const advanceMutation = api.install.advanceStep.useMutation();

  const installStatus = statusData?.status === 200 ? statusData.body : null;
  const installState = installStatus?.installationState;
  const org = installStatus?.org;

  const [currentGroupId, setCurrentGroupId] = React.useState<InstallStepGroupId>('organization');

  const nextRequired = deriveCurrentStep(installState?.steps ?? {}, billingProvider);

  const initialized = React.useRef(false);
  React.useEffect(() => {
    if (installState !== undefined && !initialized.current) {
      initialized.current = true;
      setCurrentGroupId(deriveCurrentStep(installState.steps ?? {}, billingProvider).groupId);
    }
  }, [installState, billingProvider]);

  useRefetchOnFocus(refetchStatus);

  // An open drafted screen re-reads the context until the operator edits it,
  // so a draft that lands after they arrive still fills it.
  const [draftTouched, setDraftTouched] = React.useState<ReadonlySet<DraftedGroup>>(new Set());
  const touchDraft = React.useMemo(() => {
    const mark = (group: DraftedGroup) => () =>
      setDraftTouched((prev) => (prev.has(group) ? prev : new Set(prev).add(group)));
    return {
      organization: mark('organization'),
      idp: mark('idp'),
      github_app: mark('github_app'),
      contact: mark('contact'),
    };
  }, []);
  const openDraftedGroup: DraftedGroup | null = !context
    ? null
    : !orgExists
      ? 'organization'
      : installState !== undefined &&
          isDraftedGroup(nextRequired.groupId) &&
          currentGroupId === nextRequired.groupId &&
          !isBlocked(nextRequired.step, installState, billingProvider)
        ? nextRequired.groupId
        : null;
  const rereadDraft = openDraftedGroup !== null && !draftTouched.has(openDraftedGroup);
  api.install.getContext.useQuery(
    ['install-context'],
    {},
    { queryKey: ['install-context'], refetchInterval: rereadDraft ? DRAFT_REREAD_MS : false },
  );

  const handleCreateOrg = async (input: {
    name: string;
    slug: string;
    supportedAgents: AgentKind[];
    installerEmail: string;
  }): Promise<boolean> => {
    try {
      const result = await createOrgMutation.mutateAsync({ body: input });
      if (result.status !== 201) return false;
    } catch {
      return false;
    }
    initialized.current = false;
    await refetchContext();
    await refetchStatus();
    return true;
  };

  const goToNextGroup = (from: InstallStepGroupId): void => {
    const next = nextGroupId(from, groups);
    if (next) setCurrentGroupId(next);
  };

  const handleAdvance = async (
    stepId: InstallStep,
    details?: Record<string, unknown>,
  ): Promise<AdvanceResult> => {
    let outcome: AdvanceResult;
    try {
      const result = await advanceMutation.mutateAsync({
        params: { step: stepId },
        body: { status: 'complete', ...(details ? { details } : {}) },
      });
      outcome =
        result.status === 200 ? { ok: true } : { ok: false, code: errorBodyCode(result.body) };
    } catch (err) {
      // The ts-rest client rejects on every non-2xx response and on a transport
      // failure, so a refusal reaches callers here, not as a returned status.
      outcome = { ok: false, code: thrownCode(err) };
    }
    if (!outcome.ok) return outcome;
    await refetchStatus();
    // Completing the last step a screen presents is what moves the wizard on;
    // completing an earlier one just changes what that same screen shows.
    if (isLastStepOfGroup(stepId)) goToNextGroup(groupForStep(stepId).id);
    return outcome;
  };

  // One screen drives both server steps, so Next completes whichever of the two
  // is outstanding; only `infrastructure` (the group's last) moves the wizard on.
  const handleOrganizationNext = async (): Promise<boolean> => {
    if (installState?.steps?.org_setup?.status !== 'complete') {
      if (!(await handleAdvance('org_setup')).ok) return false;
    }
    if (installState?.steps?.infrastructure?.status !== 'complete') {
      return (await handleAdvance('infrastructure')).ok;
    }
    goToNextGroup('organization');
    return true;
  };

  const handleFinish = async () => {
    const result = await handleAdvance('summary');
    if (result.ok) navigate('/apps');
  };

  if (contextLoading) {
    return (
      <div className="space-y-6" data-testid="install-loading">
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-10 w-32" />
      </div>
    );
  }

  if (contextError || !context) {
    return (
      <Alert variant="destructive" data-testid="install-error">
        <AlertDescription>
          Failed to load install context. Check that the control plane is running and try
          refreshing.
        </AlertDescription>
      </Alert>
    );
  }

  if (!orgExists) {
    return (
      <InstallStepGroupsContext.Provider value={groups}>
        <OrgSetupStep
          mode="create"
          context={context}
          onCreate={handleCreateOrg}
          onNext={async () => true}
          isSubmitting={createOrgMutation.isPending}
          draft={draft.organization}
          onDraftTouched={touchDraft.organization}
        />
      </InstallStepGroupsContext.Provider>
    );
  }

  if (statusLoading) {
    return (
      <div className="space-y-6" data-testid="install-loading">
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-10 w-32" />
      </div>
    );
  }

  if (statusError || !org) {
    return (
      <Alert variant="destructive" data-testid="install-error">
        <AlertDescription>
          Failed to load install status. Check that the control plane is running and try refreshing.
        </AlertDescription>
      </Alert>
    );
  }

  const isSubmitting = advanceMutation.isPending || createOrgMutation.isPending;

  const steps: StepConfig[] = groups.map((group) => ({
    id: group.id,
    label: group.label,
    complete: group.steps.every((s) => installState?.steps?.[s]?.status === 'complete'),
    blocked: isGroupBlocked(group.steps, installState, billingProvider),
  }));

  const finalHost = org.appsSubdomain ?? '';
  const finalUrl = finalHost ? `https://${finalHost}/install` : '';
  const onFinalOrigin =
    finalHost !== '' && window.location.protocol === 'https:' && window.location.host === finalHost;

  /** Automatic behaviors run only on the wizard's next required step. */
  const isActiveGroup = (id: InstallStepGroupId): boolean => nextRequired.groupId === id;

  return (
    <InstallStepGroupsContext.Provider value={groups}>
      <Stepper
        steps={steps}
        currentStepId={currentGroupId}
        onNavigate={(id) => setCurrentGroupId(id as InstallStepGroupId)}
      >
        {currentGroupId === 'organization' && (
          <OrgSetupStep
            mode="review"
            context={context}
            org={org}
            installationState={installState ?? {}}
            onCreate={handleCreateOrg}
            onNext={handleOrganizationNext}
            isSubmitting={isSubmitting}
          />
        )}
        {currentGroupId === 'dns_https' && (
          <DnsHttpsStep
            appsDomain={finalHost}
            cloud={context.cloud.provider}
            finalUrl={finalUrl}
            onFinalOrigin={onFinalOrigin}
            active={isActiveGroup('dns_https')}
            recordsUpserted={recordsAlreadyUpserted(installState)}
            httpsVerified={installState?.steps?.control_plane_https?.status === 'complete'}
            handoffComplete={installState?.steps?.verified_url_handoff?.status === 'complete'}
            onVerified={async () => {
              await refetchStatus();
            }}
            onHandoff={async () => {
              const result = await handleAdvance('verified_url_handoff');
              if (!result.ok) return false;
              if (!onFinalOrigin && finalUrl) window.location.href = finalUrl;
              return true;
            }}
          />
        )}
        {currentGroupId === 'idp' && (
          <IdpStep
            greenlightBaseUrl={context.greenlightBaseUrl}
            installationState={installState ?? {}}
            blocked={isBlocked('idp', installState, billingProvider)}
            onContinue={() => goToNextGroup('idp')}
            onConfigured={async () => {
              // configureIdp marks the step complete server-side; just refresh
              // the persisted state and move on.
              await refetchStatus();
              goToNextGroup('idp');
            }}
            onRefreshStatus={async () => {
              await refetchStatus();
            }}
            draft={isActiveGroup('idp') ? draft.idp : undefined}
            onDraftTouched={touchDraft.idp}
          />
        )}
        {currentGroupId === 'github_app' && (
          <GitHubAppStep
            installationState={installState ?? {}}
            scmInstallation={installStatus?.scmInstallation ?? null}
            blocked={isBlocked('github_app', installState, billingProvider)}
            active={isActiveGroup('github_app')}
            onNext={() => handleAdvance('github_app')}
            onReset={async () => {
              await refetchStatus();
            }}
            onRefresh={async () => {
              await refetchStatus();
            }}
            isSubmitting={isSubmitting}
            draft={isActiveGroup('github_app') ? draft.github : undefined}
            onDraftTouched={touchDraft.github_app}
          />
        )}
        {currentGroupId === 'plugin_marketplace' && (
          <PluginMarketplaceStep
            installationState={installState ?? {}}
            scmInstallation={installStatus?.scmInstallation ?? null}
            blocked={isBlocked('plugin_marketplace', installState, billingProvider)}
            supportedAgents={org.supportedAgents}
            onAdvance={async () => {
              // bootstrapPluginMarketplace marks the step complete server-side;
              // refresh the persisted state and move on (mirrors the idp step).
              await refetchStatus();
              goToNextGroup('plugin_marketplace');
            }}
            isSubmitting={isSubmitting}
          />
        )}
        {currentGroupId === 'contact' && (
          <ContactStep
            installationState={installState ?? {}}
            contact={installStatus?.contact ?? null}
            onSaved={async () => {
              // saveContact marks the step complete server-side; refresh the
              // persisted state and move on (mirrors the idp step).
              await refetchStatus();
              goToNextGroup('contact');
            }}
            isSubmitting={isSubmitting}
            draft={isActiveGroup('contact') ? draft.contact : undefined}
            onDraftTouched={touchDraft.contact}
          />
        )}
        {currentGroupId === 'payment' && installStepApplies('payment', billingProvider) && (
          <PaymentStep
            returned={paymentReturned}
            onContinue={async () => {
              await handleAdvance('payment');
            }}
            isSubmitting={isSubmitting}
          />
        )}
        {currentGroupId === 'summary' && (
          <SummaryStep
            org={org}
            greenlightBaseUrl={context.greenlightBaseUrl}
            installationState={installState ?? {}}
            blocked={isBlocked('summary', installState, billingProvider)}
            supportedAgents={org.supportedAgents}
            onFinish={handleFinish}
            isSubmitting={isSubmitting}
          />
        )}
      </Stepper>
    </InstallStepGroupsContext.Provider>
  );
}
